import React from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';

const HeroSection = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        minHeight: { xs: 'auto', md: '92vh' },
        borderBottom: '1px solid #1E3040',
      }}
    >
      {/* Left column */}
      <Box
        sx={{
          px: { xs: 4, md: 8 },
          py: { xs: 10, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRight: { xs: 'none', md: '1px solid #1E3040' },
          position: 'relative',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              fontSize: '10px',
              letterSpacing: '4px',
              textTransform: 'uppercase',
              color: '#FF914D',
              fontFamily: "'Barlow', sans-serif",
              mb: 3,
            }}
          >
            Eric Rak · Full-Stack Developer
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Box
            component="h1"
            sx={{
              fontFamily: "'Anton', sans-serif",
              fontSize: { xs: '64px', md: '80px', lg: '96px' },
              lineHeight: 0.9,
              letterSpacing: '-1px',
              textTransform: 'uppercase',
              color: '#F0EEE8',
              m: 0,
              mb: 4,
              '& span': { color: '#FF914D' },
            }}
          >
            Digital<br />Solutions<br /><span>That Work</span>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box
            component="p"
            sx={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 300,
              fontSize: '17px',
              color: '#7A9AB0',
              lineHeight: 1.7,
              maxWidth: '400px',
              m: 0,
              mb: 6,
            }}
          >
            Web development, mobile apps, and cloud solutions tailored for small businesses that want results — not just a website.
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box
              component="button"
              onClick={() => scrollTo('contact')}
              sx={{
                background: '#FF914D',
                color: '#0D1821',
                border: 'none',
                px: 4,
                py: 1.75,
                fontSize: '11px',
                letterSpacing: '3px',
                textTransform: 'uppercase',
                fontFamily: "'Barlow', sans-serif",
                fontWeight: 400,
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.85 },
              }}
            >
              Get Started
            </Box>
            <Box
              component="button"
              onClick={() => scrollTo('projects')}
              sx={{
                background: 'transparent',
                color: '#7A9AB0',
                border: '1px solid #2A4560',
                px: 4,
                py: 1.75,
                fontSize: '11px',
                letterSpacing: '3px',
                textTransform: 'uppercase',
                fontFamily: "'Barlow', sans-serif",
                fontWeight: 400,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#7A9AB0', color: '#F0EEE8' },
              }}
            >
              View Projects
            </Box>
          </Box>
        </motion.div>

        <Box
          sx={{
            position: 'absolute',
            bottom: 48,
            left: { xs: 4, md: 8 },
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1.5,
            fontSize: '10px',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: '#2A4560',
            fontFamily: "'Barlow', sans-serif",
            '&::before': {
              content: '""',
              display: 'block',
              width: '32px',
              height: '1px',
              background: '#2A4560',
            },
          }}
        >
          Available for projects · 2025
        </Box>
      </Box>

      {/* Right column */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          background: '#12202E',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Watermark */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            left: 24,
            fontFamily: "'Anton', sans-serif",
            fontSize: '140px',
            color: 'rgba(255,255,255,0.02)',
            lineHeight: 1,
            userSelect: 'none',
            pointerEvents: 'none',
            letterSpacing: '-2px',
          }}
        >
          RAK
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Box sx={{
            position: 'absolute', top: -28, right: -28,
            width: 72, height: 72,
            border: '1px solid rgba(255,145,77,0.3)',
            transform: 'rotate(12deg)',
          }} />
          <Box sx={{
            position: 'absolute', bottom: -20, left: -20,
            width: 44, height: 44,
            border: '1px solid rgba(255,145,77,0.15)',
            transform: 'rotate(6deg)',
          }} />
          <Box sx={{
            position: 'absolute', bottom: 0, right: -1,
            width: 1, height: 60,
            background: '#FF914D',
            opacity: 0.5,
          }} />

          <Box
            component="img"
            src="images/Ericremovebg2.png"
            alt="Eric Rak — Rak Digital Developer"
            sx={{
              width: { md: '300px', lg: '360px' },
              height: { md: '380px', lg: '460px' },
              objectFit: 'cover',
              objectPosition: 'top center',
              display: 'block',
              border: '1px solid #2A4560',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              bottom: -28,
              right: 0,
              fontSize: '9px',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: '#2A4560',
              fontFamily: "'Barlow', sans-serif",
            }}
          >
            Rak Digital Developments
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
